import {createSlice} from '@reduxjs/toolkit';
import omit from 'lodash.omit';
import {getAdminShortInfoSuccess} from 'src/openapi/admin/apis/GetAdminShortInfoApi';
import {getManagedAccountsSuccess} from 'src/openapi/admin/apis/GetManagedAccountsApi';
import {getUserLocaleSuccess} from 'src/openapi/admin/apis/GetUserLocaleApi';
import {updateUserLocaleSuccess} from 'src/openapi/admin/apis/UpdateUserLocaleApi';

import type {Locale} from '@pb/kit-context-locale';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';
import type {AppState} from 'src/store/typedef';

type InitialState = {
    userShortInfo: UserShortInfo | null,
    managedAccountIds: number[] | null,
    locale: Locale | null,
};

export const initialState: InitialState = {
    userShortInfo: null,
    managedAccountIds: null,
    locale: null,
};

export const userInfoSlice = createSlice({
    name: 'userInfoSlice',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(getAdminShortInfoSuccess, (state, action) => {
            state.userShortInfo = action.payload.data;
        });
        builder.addCase(getManagedAccountsSuccess, (state, action) => {
            const {accountManagerId} = action.payload.meta;

            if (state.userShortInfo && accountManagerId === state.userShortInfo.userId) {
                state.managedAccountIds = action.payload.data.map(account => account.accountId);
            }
        });
        builder.addCase(getUserLocaleSuccess, (state, action) => {
            const {userId} = action.payload.meta;

            if (state.userShortInfo && userId === state.userShortInfo.userId) {
                state.locale = {...action.payload.data, nativeCurrency: true};
            }
        });
        builder.addCase(updateUserLocaleSuccess, (state, action) => {
            const {userId, userLocaleSaveRequest: locale} = action.payload.meta;

            if (state.userShortInfo && userId === state.userShortInfo.userId) {
                state.locale = {
                    ...omit(locale, 'reason'),
                    nativeCurrency: true,
                };
            }
        });
    },
});

type SelectUserShortInfo = (state: AppState) => UserShortInfo | null;
export const selectUserShortInfo: SelectUserShortInfo = state => state.userInfo.userShortInfo;

type SelectManagedAccountIds = (state: AppState) => number[] | null;
export const selectManagedAccountIds: SelectManagedAccountIds = state => state.userInfo.managedAccountIds;

export const adminLocaleSelector = (state: AppState) =>
    state.userInfo.locale;
